/**
 * Utility functions for image SEO optimization
 */

export interface ImageSEOData {
  src: string;
  alt: string;
  title?: string;
  width?: number;
  height?: number;
  context?: string; // Page or section context for auto-generation
}

/**
 * Optimize filename for SEO (lowercase, hyphenated)
 */
export const optimizeFilename = (filename: string): string => {
  return filename
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
    .replace(/[^a-z0-9.-]/g, '')
    .replace(/-+/g, '-');
};

/**
 * Generate SEO-friendly image title
 */
export const generateImageTitle = (
  filename: string,
  context?: string,
  businessName: string = "Capital District Social"
): string => {
  // Remove file extension and optimize
  const baseName = filename.replace(/\.[^/.]+$/, '');
  const cleaned = baseName
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  if (context) {
    return `${businessName} – ${context} – ${cleaned}`;
  }
  
  return `${businessName} – ${cleaned}`;
};

/**
 * Generate descriptive ALT text with keywords
 */
export const generateAltText = (
  filename: string,
  context?: string,
  keywords: string[] = ['American bar and restaurant', 'Tallahassee FL']
): string => {
  const baseName = filename.replace(/\.[^/.]+$/, '');
  const cleaned = baseName
    .replace(/[-_]/g, ' ')
    .toLowerCase();

  const contextPart = context ? `${context} - ` : '';
  const keywordPart = keywords.length > 0 ? ` - ${keywords[0]}` : '';
  
  return `${contextPart}${cleaned}${keywordPart}`.trim();
};

/**
 * Generate ImageObject structured data
 */
export const generateImageStructuredData = (
  imageData: ImageSEOData,
  siteUrl: string = 'https://capitaldistrictsocial.com'
): object => {
  const fullUrl = imageData.src.startsWith('http') 
    ? imageData.src 
    : `${siteUrl}${imageData.src}`;

  return {
    "@type": "ImageObject",
    "name": imageData.title || generateImageTitle(imageData.src, imageData.context),
    "contentUrl": fullUrl,
    "url": fullUrl,
    "description": imageData.alt,
    "author": {
      "@type": "Organization",
      "name": "Capital District Social"
    },
    ...(imageData.width && { "width": `${imageData.width}px` }),
    ...(imageData.height && { "height": `${imageData.height}px` }),
    "inLanguage": "en-US"
  };
};

/**
 * Extract filename from path or URL
 */
export const extractFilename = (src: string): string => {
  return src.split('/').pop() || src;
};